// =============================================================================
// Admin Analytics — mock adapter (Activation only)
// =============================================================================
// Fulfils `AdminAnalyticsAdapter` by aggregating the seeded fixture pool in the
// browser, honoring the date range so the per-widget time overrides visibly
// change the numbers. The live route uses `useGraphqlAdminAnalyticsAdapter()`
// (`graphqlAdapter.ts`) instead; this mock is the provider's default and is what
// the Vitest suite exercises. The Reports tab doesn't go through an adapter.
// =============================================================================
import { dataset } from "@pages/AdminAnalytics/data/fixtures";
import {
  ActivationProviderRow,
  AdminAnalyticsAdapter,
  DashboardFilters,
  DateRangeContract,
  InviteFunnel,
} from "@pages/AdminAnalytics/data/types";

/** Simulated network latency so loading skeletons are exercised in the prototype. */
const LATENCY_MS = 420;
const settle = <T>(value: T): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), LATENCY_MS));

const inRange = (isoDate: string | undefined, range: DateRangeContract): boolean => {
  if (!isoDate) return false;
  return isoDate >= range.start && isoDate <= range.end;
};

export class MockAdminAnalyticsAdapter implements AdminAnalyticsAdapter {
  async getInviteFunnel({ range }: DashboardFilters): Promise<InviteFunnel> {
    const invited = dataset.providers.filter((p) => inRange(p.invitedAt, range));
    return settle({
      invited: invited.length,
      expired: invited.filter((p) => p.inviteExpired).length,
      loggedIn: invited.filter((p) => p.hasLoggedIn).length,
      notYetLoggedIn: invited.filter((p) => !p.hasLoggedIn).length,
    });
  }

  async getActivationProviders({ range }: DashboardFilters): Promise<ActivationProviderRow[]> {
    const resolveStatus = (p: (typeof dataset.providers)[0]): ActivationProviderRow["status"] => {
      if (p.inviteExpired) return "expired";
      if (p.hasLoggedIn) return "accepted";
      return "sent";
    };
    const rows: ActivationProviderRow[] = dataset.providers
      .filter((p) => inRange(p.invitedAt, range))
      .map(
        (p): ActivationProviderRow => ({
          providerId: p.providerId,
          name: p.name,
          email: p.email,
          invitedAt: p.invitedAt,
          status: resolveStatus(p),
          lastLoginAt: p.lastLoginAt,
          hasLoggedIn: p.hasLoggedIn,
        }),
      )
      .sort((a, b) => b.invitedAt.localeCompare(a.invitedAt));
    return settle(rows);
  }
}

export const mockAdminAnalyticsAdapter = new MockAdminAnalyticsAdapter();
